import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Calculator } from '@lifeomic/chromicons';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import SurveyItem from '../util/SurveyItem';

const messages = defineMessages({
  indicator: {
    id: 'surveys.calculatedValue.indicator',
    defaultMessage: 'Calculated automatically',
  },
});

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    // Sit after the question title on the same row (not a caption above the
    // field). Spacing aligns with Chroma TextField's labelIcon treatment and
    // the 14px icon size used by FilledByIndicator.
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: theme.spacing(0.75),
    color: theme.palette.primary.main,
    cursor: 'help',
    verticalAlign: 'middle',
  },
  icon: {
    width: theme.pxToRem(14),
    height: theme.pxToRem(14),
  },
}));

export const testIds = {
  indicator: (linkId: string) => `survey-calculatedValue-${linkId}`,
};

export type CalculatedValueIndicatorProps = {
  surveyItem: SurveyItem;
};

/**
 * Icon adornment for calculated-value fields. Renders after the question
 * title; hover and aria-label explain that the value is derived automatically.
 */
const CalculatedValueIndicator: React.FC<CalculatedValueIndicatorProps> = ({
  surveyItem,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  if (!surveyItem.hasCalculatedValue) return null;

  const label = formatMessage(messages.indicator);

  return (
    <Tooltip title={label}>
      <span
        className={classes.root}
        aria-label={label}
        data-testid={testIds.indicator(surveyItem.link)}
      >
        <Calculator aria-hidden className={classes.icon} />
      </span>
    </Tooltip>
  );
};

export default CalculatedValueIndicator;

/**
 * Question title plus optional calculated-value calculator icon.
 * Use wherever a control renders `surveyItem.label` as its header.
 */
export const surveyItemHeaderLabel = (
  surveyItem: SurveyItem,
  label: string | undefined,
): React.ReactNode => {
  if (!label) return label;
  if (!surveyItem.hasCalculatedValue) return label;
  return (
    <>
      {label}
      <CalculatedValueIndicator surveyItem={surveyItem} />
    </>
  );
};
